import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { AxiosInstance } from 'axios';

import { useAppDispatch, useAppSelector } from '../../hooks/hooksRedux';
import { addPersonalitySlug, markLanguagePersisted } from '../../store/globalSlice/languageSlice/LanguageSlice';
import { useAuth } from '../../store/auth';
import { RootState } from '../../store';

import { OptionWithIcon } from '../../interfaces/OptionWithIcon';

import { CustomSelectWithIcon } from '../inputs';

import { AccountService } from '../../services/AccountService';

import { errorToast } from '../../utils/toasts';

import { errorMessageAdapter } from '../../adapters';
import { useApi } from '../../services/hooks/apiHook';
import { DashboardCustomerContext } from '../../pages/DashboardCustomer/Context';

const languages = [
  { label: 'EN', value: 'en', src: '/svg/flags/en.svg' },
  { label: 'PT', value: 'pt-BR', src: '/svg/flags/br.svg' },
  { label: 'ES', value: 'es', src: '/svg/flags/es.svg' }
];

const LanguageSelect = () => {
  const { i18n } = useTranslation();
  const { token } = useAuth();
  const location = useLocation();
  const api: AxiosInstance = useApi();

  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);

  const [selectedItem, setSelectedItem] = useState<OptionWithIcon>(languages[0]);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const { refreshNotifications, setNotificationsRefreshing } = useContext(DashboardCustomerContext);

  const fetchLanguagePersistence = useCallback(
    async (selectedLanguage: string) => {
      if (!token) {
        return;
      }

      try {
        await AccountService.updatedLanguageState(
          api,
          { preferred_language: selectedLanguage },
          {
            headers: { 'Content-Language': selectedLanguage, Authorization: `Bearer ${token}` }
          }
        );
        dispatch(markLanguagePersisted({ language: selectedLanguage, at: Date.now() }));
      } catch (error) {
        console.error(error);
        errorToast(errorMessageAdapter(error));
      }
    },
    [api, token, dispatch]
  );

  const handleChangeOption = useCallback(
    (option: OptionWithIcon) => {
      if (option.value === legalPersonalitySlug) {
        return;
      }

      setPendingLanguage(option.value);
      dispatch(addPersonalitySlug(option.value));
      void fetchLanguagePersistence(option.value);
      setSelectedItem(option);
      void i18n.changeLanguage(option.value);

      if (location.pathname.startsWith('/dashboard')) {
        setNotificationsRefreshing(true);
        setTimeout(() => {
          void refreshNotifications(true);
        }, 700);
      }
    },
    [dispatch, fetchLanguagePersistence, i18n, legalPersonalitySlug, location.pathname, refreshNotifications, setNotificationsRefreshing]
  );

  useEffect(() => {
    if (!legalPersonalitySlug) {
      return;
    }

    const option = languages.find((lang) => lang.value === legalPersonalitySlug) ?? languages[0];

    setSelectedItem(option);

    if (i18n.language !== option.value) {
      void i18n.changeLanguage(option.value);
    }

    if (pendingLanguage === legalPersonalitySlug) {
      setPendingLanguage(null);
    }
  }, [legalPersonalitySlug, i18n, pendingLanguage]);

  return <CustomSelectWithIcon selectedItem={selectedItem} setSelectedItem={handleChangeOption} items={languages} />;
};

export { LanguageSelect };
